import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { getStoreDownloadUrl } from "../../../config/utils";
import { ClavaContext } from "../../../config/contexts";
import { translate } from "../../../config/translator";
import RoundedRectangle from "../RoundedRectangle";
import ScaleFade from "../Animated/ScaleFade";

type StoreMiniBannerProps = {
  className?: string;
  message?: string;
  onClose?: () => void;
  autoHideMs?: number;
  compact?: boolean;
  noBorder?: boolean;
  animated?: boolean;
};

const StoreMiniBanner: React.FC<StoreMiniBannerProps> = ({
                                                           className,
                                                           message,
                                                           onClose,
                                                           autoHideMs = 6000,
                                                           compact,
                                                           noBorder,
                                                           animated
                                                         }) => {
  const { l } = useContext(ClavaContext);

  const [open, setOpen] = useState(false);

  const bannerRef = useRef<HTMLDivElement>(null);

  const onCloseRef = useRef<typeof onClose>();
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const close = useCallback(() => {
    setOpen(false);
    onCloseRef.current?.();
  }, []);

  useEffect(() => {
    let rafId: number | undefined;
    if (typeof window !== "undefined") {
      rafId = window.requestAnimationFrame(() => setOpen(true));
    }
    return () => {
      if (rafId !== undefined) window.cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    let timeoutId: number | undefined;
    if (open && autoHideMs > 0) {
      timeoutId = window.setTimeout(() => {
        close();
      }, autoHideMs);
    }
    return () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [open, autoHideMs, close]);

  useEffect(() => {
    const ac = new AbortController();
    if (open && typeof document !== "undefined") {
      const handlePointerDown = (e: PointerEvent) => {
        const path = e.composedPath();
        if (bannerRef.current && !path.includes(bannerRef.current)) {
          close();
        }
      };

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") close();
      };

      document.addEventListener("pointerdown", handlePointerDown, {
        capture: true,
        signal: ac.signal
      });
      document.addEventListener("keydown", handleKeyDown, { signal: ac.signal });
    }
    return () => {
      ac.abort();
    };
  }, [open, close]);

  const shouldAnimate = animated ?? true;

  return (
    <ScaleFade isOpen={shouldAnimate ? open : true} className={className}>
      <RoundedRectangle
        ref={bannerRef}
        className={`${
          compact ? "px-4 py-3 w-48" : "px-6 py-4 w-56"
        } bg-white dark:bg-bg-secondary-dark shadow-shadow dark:shadow-shadow-dark drop-shadow-md ${
          noBorder ? "" : "border border-light-gray dark:border-light-gray-dark"
        }`}
        hasBorder={!noBorder}
        hasTitle={false}
      >
        <div className="relative">
          <button
            type="button"
            aria-label={translate("close", l)}
            className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-font-600 dark:bg-font-600-dark text-white text-xs font-bold flex items-center justify-center hover:bg-font-700 dark:hover:bg-font-700-dark"
            onClick={close}
          >
            <FontAwesomeIcon icon={faXmark} className="w-3 h-3" />
          </button>
          <div className="flex flex-col items-center gap-2 text-center pr-6">
            <p className={`${compact ? "text-xs" : "text-xs"} text-font dark:text-font-dark font-regular`}>
              {message ?? translate("downloadUnlimited", l)}
            </p>
            {!compact && (
              <p className="text-xs text-font-600 dark:text-font-600-dark">
                {translate("downloadNow", l)} – iOS
              </p>
            )}
            <a
              href={getStoreDownloadUrl()}
              className={`inline-flex items-center justify-center ${
                compact ? "h-7 px-3 text-xs" : "h-8 px-4 text-xs"
              } rounded-md bg-primary text-white font-semibold hover:opacity-90 transition-opacity`}
            >
              {translate("downloadNow", l)}
            </a>
          </div>
        </div>
      </RoundedRectangle>
    </ScaleFade>
  );
};

export default StoreMiniBanner;

